import React, { useState } from 'react';
import PropTypes from 'prop-types';

/**
 * ImageDebugPanel - Visual debugging for CLIP image filtering
 *
 * Shows all image candidates with their CLIP scores, filter reasons,
 * and OCR previews. Only displayed in development environment.
 *
 * Color coding:
 * - Green: Selected (passed threshold)
 * - Yellow: Close to threshold (0.05-0.08)
 * - Red: Filtered out (below threshold or hard filtered)
 */
const ImageDebugPanel = ({ debugData, threshold = 0.08 }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Debug logging
  console.log('🔬 [ImageDebugPanel] Component called:', {
    hasDebugData: !!debugData,
    debugDataType: typeof debugData,
    debugDataLength: Array.isArray(debugData) ? debugData.length : 'not an array',
    viteMode: import.meta.env.MODE,
    isDev: import.meta.env.DEV,
    isProduction: import.meta.env.MODE === 'production'
  });

  // Don't render in production
  // Vite uses import.meta.env.MODE instead of process.env.NODE_ENV
  if (import.meta.env.MODE === 'production') {
    console.log('🔬 [ImageDebugPanel] Skipped: production mode');
    return null;
  }

  // Don't render if no debug data
  if (!debugData || debugData.length === 0) {
    console.log('🔬 [ImageDebugPanel] Skipped: no debug data or empty array');
    return null;
  }

  console.log('🔬 [ImageDebugPanel] ✅ RENDERING with', debugData.length, 'images');

  // Calculate stats
  const totalImages = debugData.length;
  const selectedImages = debugData.filter(img => img.passed_threshold).length;
  const filteredImages = totalImages - selectedImages;
  const avgScore = debugData
    .filter(img => img.similarity_score !== null)
    .reduce((sum, img) => sum + img.similarity_score, 0) /
    debugData.filter(img => img.similarity_score !== null).length;

  // Determine row color based on status with more granular scale
  const getRowClass = (img) => {
    if (img.passed_threshold) {
      return 'bg-green-50 border-green-200';
    }
    const score = img.similarity_score;
    if (score !== null) {
      if (score >= 0.12) {
        return 'bg-lime-50 border-lime-200'; // Very close to threshold
      }
      if (score >= 0.08) {
        return 'bg-yellow-50 border-yellow-200'; // Close to threshold
      }
      if (score >= 0.05) {
        return 'bg-orange-50 border-orange-200'; // Somewhat relevant
      }
    }
    return 'bg-red-50 border-red-200'; // Filtered out
  };

  // Get status badge with granular scale
  const getStatusBadge = (img) => {
    if (img.error) {
      return <span className="px-2 py-1 text-xs font-semibold rounded bg-red-600 text-white">ERROR</span>;
    }
    if (img.passed_threshold) {
      return <span className="px-2 py-1 text-xs font-semibold rounded bg-green-600 text-white">SELECTED</span>;
    }
    const score = img.similarity_score;
    if (score !== null) {
      if (score >= 0.12) {
        return <span className="px-2 py-1 text-xs font-semibold rounded bg-lime-600 text-white">VERY CLOSE</span>;
      }
      if (score >= 0.08) {
        return <span className="px-2 py-1 text-xs font-semibold rounded bg-yellow-600 text-white">CLOSE</span>;
      }
      if (score >= 0.05) {
        return <span className="px-2 py-1 text-xs font-semibold rounded bg-orange-600 text-white">MARGINAL</span>;
      }
    }
    return <span className="px-2 py-1 text-xs font-semibold rounded bg-red-600 text-white">FILTERED</span>;
  };

  // Format score display
  const formatScore = (score) => {
    if (score === null || score === undefined) {
      return 'N/A';
    }
    return score.toFixed(4);
  };

  // Get filter reason or error
  const getFilterInfo = (img) => {
    if (img.error) {
      return img.error;
    }
    if (img.filter_reason) {
      return img.filter_reason;
    }
    if (img.passed_threshold) {
      return 'Passed all filters';
    }
    if (img.similarity_score !== null) {
      return `Score below threshold (${formatScore(img.similarity_score)} < ${threshold})`;
    }
    return 'Unknown';
  };

  return (
    <div className="mt-8 p-4 border-2 border-blue-500 rounded-lg bg-blue-50">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-bold text-blue-900">
            🔬 Image Debug Panel (Dev Only)
          </h3>
          <div className="flex gap-2 text-sm">
            <span className="px-3 py-1 bg-white rounded border border-blue-300">
              Total: <strong>{totalImages}</strong>
            </span>
            <span className="px-3 py-1 bg-green-100 rounded border border-green-300">
              Selected: <strong>{selectedImages}</strong>
            </span>
            <span className="px-3 py-1 bg-red-100 rounded border border-red-300">
              Filtered: <strong>{filteredImages}</strong>
            </span>
            <span className="px-3 py-1 bg-white rounded border border-blue-300">
              Avg Score: <strong>{formatScore(avgScore)}</strong>
            </span>
          </div>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        >
          {isExpanded ? 'Hide Details' : 'Show Details'}
        </button>
      </div>

      {/* Collapsible content */}
      {isExpanded && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-blue-100 border-b-2 border-blue-300">
                <th className="p-2 text-left font-semibold">Preview</th>
                <th className="p-2 text-left font-semibold">URL</th>
                <th className="p-2 text-center font-semibold">CLIP Score</th>
                <th className="p-2 text-center font-semibold">Status</th>
                <th className="p-2 text-left font-semibold">Filter Info</th>
                <th className="p-2 text-left font-semibold">OCR Preview</th>
              </tr>
            </thead>
            <tbody>
              {debugData.map((img, idx) => (
                <tr key={idx} className={`border-b border-gray-300 ${getRowClass(img)}`}>
                  {/* Preview */}
                  <td className="p-2">
                    <img
                      src={img.src_url}
                      alt={img.alt_text || 'Image preview'}
                      className="w-80 h-20 object-cover rounded border border-gray-300"
                      loading="lazy"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="320" height="80"><rect width="320" height="80" fill="%23ddd"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%23999">No image</text></svg>';
                      }}
                    />
                  </td>

                  {/* URL */}
                  <td className="p-2 max-w-[150px]">
                    <a
                      href={img.src_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-800 hover:underline font-mono block truncate"
                      title={img.src_url}
                    >
                      {img.src_url}
                    </a>
                    {img.alt_text && (
                      <div className="text-xs text-gray-600 mt-1 italic truncate">
                        Alt: {img.alt_text.substring(0, 30)}
                        {img.alt_text.length > 30 ? '...' : ''}
                      </div>
                    )}
                  </td>

                  {/* CLIP Score */}
                  <td className="p-2 text-center">
                    <span className={`font-mono font-semibold ${
                      img.similarity_score === null ? 'text-gray-400' :
                      img.similarity_score >= threshold ? 'text-green-700' :
                      img.similarity_score >= 0.12 ? 'text-lime-700' :
                      img.similarity_score >= 0.08 ? 'text-yellow-700' :
                      img.similarity_score >= 0.05 ? 'text-orange-700' :
                      'text-red-700'
                    }`}>
                      {formatScore(img.similarity_score)}
                    </span>
                    {img.context_used && (
                      <div className="text-xs text-gray-500 mt-1">
                        ({img.context_used})
                      </div>
                    )}
                  </td>

                  {/* Status Badge */}
                  <td className="p-2 text-center">
                    {getStatusBadge(img)}
                  </td>

                  {/* Filter Info */}
                  <td className="p-2 text-xs">
                    <div className={`${
                      img.error ? 'text-red-700' :
                      img.passed_threshold ? 'text-green-700' :
                      'text-gray-700'
                    }`}>
                      {getFilterInfo(img)}
                    </div>
                  </td>

                  {/* OCR Preview */}
                  <td className="p-2 max-w-xs">
                    {(() => {
                      console.log('🔬 [ImageDebugPanel] OCR Debug:', {
                        idx,
                        src_url: img.src_url.substring(0, 50),
                        has_ocr_text: !!img.ocr_text,
                        ocr_text_type: typeof img.ocr_text,
                        ocr_text_value: img.ocr_text,
                        all_keys: Object.keys(img)
                      });
                      return null;
                    })()}
                    {img.ocr_text ? (
                      <div className="text-xs text-gray-600 truncate font-mono">
                        {img.ocr_text.substring(0, 60)}
                        {img.ocr_text.length > 60 ? '...' : ''}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-400 italic">No OCR text</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Legend */}
          <div className="mt-4 p-3 bg-white rounded border border-blue-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Legend (Threshold: {threshold}):</h4>
            <div className="grid grid-cols-5 gap-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-200 border border-green-400 rounded"></div>
                <span>Selected (≥0.15)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-lime-200 border border-lime-400 rounded"></div>
                <span>Very Close (0.12-0.15)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-yellow-200 border border-yellow-400 rounded"></div>
                <span>Close (0.08-0.12)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-orange-200 border border-orange-400 rounded"></div>
                <span>Marginal (0.05-0.08)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-200 border border-red-400 rounded"></div>
                <span>Filtered (&lt;0.05)</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

ImageDebugPanel.propTypes = {
  debugData: PropTypes.arrayOf(PropTypes.shape({
    src_url: PropTypes.string.isRequired,
    alt_text: PropTypes.string,
    similarity_score: PropTypes.number,
    passed_threshold: PropTypes.bool,
    error: PropTypes.string,
    filter_reason: PropTypes.string,
    context_used: PropTypes.string,
    ocr_text: PropTypes.string,
  })),
  threshold: PropTypes.number,
};

export default ImageDebugPanel;
